import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Book, Clock, CheckCircle2, Circle, ChevronLeft, ChevronRight, Award, Play, FileText, ArrowLeft, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Lesson {
  id: string;
  title: string;
  description: string;
  duration: string;
  completed: boolean;
  content: {
    introduction: string;
    keyPoints: string[];
    detailedContent: string[];
    quiz?: {
      question: string;
      options: string[];
      correctAnswer: number;
    }[];
  };
}

interface Course {
  id: string;
  title: string;
  description: string;
  lessons: Lesson[];
}

const coursesData: Course[] = [
  {
    id: 'beginner',
    title: 'Módulo 1: Fundamentos de Bitcoin',
    description: 'Historia, filosofía y conceptos básicos - Bitcoin Life Panamá',
    lessons: [
      {
        id: '1',
        title: '¿Qué es Bitcoin?',
        description: 'Una introducción a la primera moneda digital descentralizada del mundo',
        duration: '15 min',
        completed: false,
        content: {
          introduction: 'Bitcoin es una moneda digital revolucionaria que opera sin una autoridad central. Creada en 2009 por una persona o grupo anónimo bajo el nombre de Satoshi Nakamoto, Bitcoin introdujo una nueva forma de transferir valor por internet.',
          keyPoints: [
            'Bitcoin es una moneda digital descentralizada',
            'Opera en una red peer-to-peer (entre pares)',
            'Ninguna autoridad central o gobierno la controla',
            'Las transacciones son verificadas por nodos de red mediante criptografía',
            'Suministro limitado de 21 millones de monedas'
          ],
          detailedContent: [
            'Bitcoin representa un cambio de paradigma en cómo pensamos sobre el dinero. A diferencia de las monedas tradicionales emitidas por gobiernos (monedas fiat), Bitcoin se crea y mantiene electrónicamente. Nadie la controla, y las monedas no se imprimen como dólares o euros – son producidas por personas que ejecutan computadoras alrededor del mundo usando software que resuelve problemas matemáticos.',
            'La red de Bitcoin está completamente descentralizada, lo que significa que no hay un servidor central o autoridad controladora. Esto es fundamentalmente diferente de los sistemas bancarios tradicionales donde una autoridad central controla la oferta monetaria y valida las transacciones.',
            'Cada transacción de Bitcoin se registra en un libro público llamado blockchain. Este libro es mantenido por miles de computadoras alrededor del mundo, haciéndolo casi imposible de hackear o manipular. El blockchain asegura que el mismo Bitcoin no pueda gastarse dos veces – resolviendo el problema del "doble gasto" que afectó a intentos anteriores de moneda digital.',
            'La escasez de Bitcoin está integrada en su código. Solo existirán 21 millones de Bitcoin, con nuevas monedas siendo creadas a través de un proceso llamado minería a una tasa predecible y decreciente. Esta escasez predeterminada es similar a metales preciosos como el oro, por lo cual Bitcoin a menudo se llama "oro digital".',
            'Las implicaciones de Bitcoin se extienden mucho más allá de solo una nueva forma de dinero. Representa soberanía financiera, permitiendo a las personas tener control completo sobre su riqueza sin depender de bancos o gobiernos. Esto es particularmente poderoso en países con monedas inestables o sistemas financieros restrictivos.'
          ],
          quiz: [
            {
              question: '¿Qué hace a Bitcoin diferente de las monedas tradicionales?',
              options: [
                'Es controlada por un banco central',
                'Opera en una red descentralizada',
                'Puede ser impresa bajo demanda',
                'Requiere aprobación gubernamental'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Cuál es el suministro máximo de Bitcoin?',
              options: [
                'Ilimitado',
                '100 millones',
                '21 millones',
                '1 mil millones'
              ],
              correctAnswer: 2
            }
          ]
        }
      },
      {
        id: '2',
        title: 'Entendiendo la Escasez Digital',
        description: 'Aprende por qué la escasez importa en las monedas digitales',
        duration: '18 min',
        completed: false,
        content: {
          introduction: 'La escasez digital es uno de los conceptos más revolucionarios de Bitcoin. Antes de Bitcoin, los elementos digitales podían copiarse infinitamente sin costo. Bitcoin resolvió este problema, creando verdadera escasez digital por primera vez en la historia.',
          keyPoints: [
            'La escasez digital significa que algo digital no puede ser duplicado',
            'El suministro de Bitcoin está limitado a 21 millones de monedas',
            'La escasez le da a Bitcoin su propuesta de valor',
            'La minería crea nuevo Bitcoin en un calendario predecible',
            'El halving reduce el suministro de nuevo Bitcoin cada 4 años'
          ],
          detailedContent: [
            'En el mundo físico, la escasez es natural. Solo hay cierta cantidad de oro en la tierra, solo cierta cantidad de tierra en el planeta. Pero en el ámbito digital, todo puede ser copiado perfectamente e infinitamente. Puedes copiar una foto, un documento o un archivo de música sin degradación y prácticamente sin costo. Esto es excelente para compartir información pero terrible para crear dinero digital.',
            'Bitcoin resolvió el problema de la escasez digital a través de su tecnología blockchain y mecanismo de consenso de prueba de trabajo. Cuando posees Bitcoin, la red acuerda colectivamente que tú y solo tú posees esa cantidad específica. No puede ser duplicado o falsificado porque cada transacción es verificada por miles de computadoras ejecutando el software de Bitcoin.',
            'La escasez de Bitcoin está codificada en su protocolo. Cada 10 minutos, en promedio, se crea nuevo Bitcoin como recompensa para los mineros que aseguran la red. Esta recompensa comenzó en 50 Bitcoin por bloque en 2009 y se reduce a la mitad aproximadamente cada cuatro años en un evento llamado "el halving". A partir de 2024, la recompensa por bloque es de 3.125 Bitcoin.',
            'Esta emisión predecible y decreciente significa que sabemos exactamente cuántos Bitcoin existirán en cualquier punto del futuro. Para el año 2140, todos los 21 millones de Bitcoin habrán sido minados, y nunca se crearán nuevas monedas. Esto hace que Bitcoin sea potencialmente deflacionario – a diferencia de las monedas fiat que pueden imprimirse indefinidamente.',
            'La escasez digital combinada con accesibilidad global crea una poderosa propuesta de valor. Bitcoin no puede ser inflado por política gubernamental, confiscado fácilmente, o falsificado. Esto lo hace un atractivo depósito de valor, particularmente en un mundo cada vez más digital donde la posesión física se vuelve menos relevante.'
          ],
          quiz: [
            {
              question: '¿Qué es el halving?',
              options: [
                'Cuando el precio de Bitcoin cae a la mitad',
                'Cuando la recompensa por bloque minero se reduce a la mitad',
                'Cuando la mitad de todos los Bitcoin son minados',
                'Cuando las comisiones de transacción se reducen'
              ],
              correctAnswer: 1
            }
          ]
        }
      },
      {
        id: '3',
        title: 'Cómo Funciona el Blockchain',
        description: 'Comprende la tecnología detrás de Bitcoin',
        duration: '22 min',
        completed: false,
        content: {
          introduction: 'El blockchain es la tecnología fundamental de Bitcoin – un libro mayor distribuido que registra todas las transacciones de manera segura, transparente e inmutable.',
          keyPoints: [
            'Blockchain es una cadena de bloques que contienen datos de transacciones',
            'Cada bloque está enlazado criptográficamente al anterior',
            'El blockchain es mantenido por una red distribuida',
            'La prueba de trabajo asegura la red',
            'Una vez confirmadas, las transacciones no pueden revertirse'
          ],
          detailedContent: [
            'Piensa en el blockchain como un libro de contabilidad digital que registra cada transacción de Bitcoin jamás realizada. Pero a diferencia de un libro tradicional mantenido por un banco, el blockchain es público, distribuido a través de miles de computadoras en todo el mundo, y asegurado por criptografía avanzada.',
            'Cada "bloque" en el blockchain contiene un lote de transacciones. Cuando alguien envía Bitcoin, esa transacción se transmite a la red y se agrupa con otras transacciones pendientes. Los mineros entonces compiten para validar estas transacciones y agregarlas al blockchain en un nuevo bloque.',
            'Lo que hace al blockchain inmutable es cómo cada bloque está enlazado al anterior. Cada bloque contiene un hash criptográfico del bloque previo, creando una cadena irrompible. Si alguien intentara alterar una transacción antigua, cambiaría el hash de ese bloque, rompiendo la cadena y alertando a la red de la manipulación.',
            'El sistema de prueba de trabajo agrega otra capa de seguridad. Para agregar un nuevo bloque, los mineros deben resolver un rompecabezas computacionalmente difícil. Esto requiere poder computacional significativo y electricidad, haciéndolo prohibitivamente caro atacar la red. Para alterar exitosamente el blockchain, un atacante necesitaría controlar más del 50% del poder computacional de la red – una hazaña que se vuelve más difícil y costosa a medida que la red crece.',
            'Este sistema elegante crea un entorno sin necesidad de confianza. No necesitas confiar en un banco, gobierno, o cualquier tercero para saber que tu transacción de Bitcoin es válida y permanente. El blockchain mismo, mantenido por miles de nodos independientes, proporciona esa certeza a través de matemáticas y criptografía en lugar de autoridad institucional.'
          ]
        }
      },
      {
        id: '4',
        title: 'Billeteras y Claves',
        description: 'Aprende cómo almacenar y gestionar tu Bitcoin de forma segura',
        duration: '20 min',
        completed: false,
        content: {
          introduction: 'Entender las billeteras y claves de Bitcoin es crucial para gestionar tu Bitcoin de forma segura. Tus claves son tu Bitcoin – piérdelas, y pierdes acceso a tus fondos para siempre.',
          keyPoints: [
            'Una billetera es software que gestiona tus claves de Bitcoin',
            'Las claves privadas prueban la propiedad de Bitcoin',
            'Las claves públicas son como tu número de cuenta',
            'Las frases semilla pueden recuperar tu billetera',
            'Las billeteras de hardware proporcionan máxima seguridad'
          ],
          detailedContent: [
            'Una billetera de Bitcoin realmente no almacena Bitcoin – almacena las claves privadas que prueban que posees Bitcoin en el blockchain. Piensa en ella como un llavero que sostiene las llaves de varias cajas de seguridad. El Bitcoin mismo nunca se mueve del blockchain; la propiedad se transfiere usando tu clave privada para firmar transacciones.',
            'Tu clave privada es un número secreto que te permite gastar tu Bitcoin. Cualquiera que tenga tu clave privada tiene control completo sobre tu Bitcoin, por lo cual mantenerla segura es primordial. Nunca compartas tu clave privada con nadie, y ten extremo cuidado sobre dónde la almacenas.',
            'La clave pública se deriva de tu clave privada a través de funciones criptográficas unidireccionales. Es seguro compartir tu clave pública (o más comúnmente, tu dirección Bitcoin, que se deriva de tu clave pública) porque no puede usarse para determinar tu clave privada o gastar tu Bitcoin. Piensa en ella como tu dirección de correo electrónico – puedes compartirla libremente para recibir fondos.',
            'La mayoría de billeteras modernas usan una frase semilla (también llamada frase de recuperación o frase mnemónica) – típicamente 12 o 24 palabras que pueden regenerar todas tus claves privadas. Esta frase es crucial para recuperar tu billetera si tu dispositivo se pierde o daña. Escríbela en papel, almacénala de forma segura, y nunca la guardes digitalmente donde pueda ser hackeada.',
            'Diferentes tipos de billeteras ofrecen diferentes compromisos entre seguridad y conveniencia. Las billeteras de software en tu teléfono o computadora son convenientes pero vulnerables a malware. Las billeteras de hardware (dispositivos físicos dedicados) proporcionan la mejor seguridad al mantener tus claves privadas fuera de línea. Para grandes cantidades, considera usar una billetera de hardware o incluso una configuración multifirma que requiera múltiples claves para gastar.'
          ]
        }
      },
      {
        id: '5',
        title: 'Teoría Económica Austriaca y Bitcoin',
        description: 'Fundamentos económicos que validan el modelo de Bitcoin',
        duration: '30 min',
        completed: false,
        content: {
          introduction: 'Bitcoin no surgió del vacío - está fundamentado en principios económicos sólidos de la Escuela Austriaca de Economía. Entender esta teoría económica te ayudará a comprender por qué Bitcoin es importante.',
          keyPoints: [
            'La acción humana y la preferencia temporal',
            'El dinero surge del mercado, no del gobierno',
            'La inflación es un impuesto oculto',
            'El patrón oro y su abandono',
            'Bitcoin como dinero del mercado libre'
          ],
          detailedContent: [
            'La Escuela Austriaca de Economía, representada por pensadores como Carl Menger, Ludwig von Mises y Friedrich Hayek, explica cómo el dinero emerge naturalmente del mercado. Según Carl Menger, el dinero no fue inventado por decreto gubernamental, sino que evolucionó orgánicamente como el bien más comerciable - el bien que más fácilmente puedes intercambiar por otros bienes.',
            'Un concepto clave es la preferencia temporal: los humanos preferimos tener bienes ahora que en el futuro. El dinero "duro" (escaso) incentiva el ahorro porque mantiene su valor. El dinero "fácil" (abundante) incentiva el gasto porque pierde valor con el tiempo. Esta es la raíz de la diferencia entre el oro (dinero duro) y el dólar (dinero fácil que se imprime constantemente).',
            'La inflación monetaria - aumentar la oferta de dinero - es efectivamente un impuesto invisible. Cuando un banco central imprime más dinero, no crea riqueza; simplemente diluye el valor del dinero existente. Es como si todos tus billetes perdieran poder adquisitivo simultáneamente. Este "impuesto inflacionario" afecta más a quienes menos tienen, ya que no pueden protegerse invirtiendo en activos que resisten la inflación.',
            'El abandono del patrón oro en 1971 eliminó las restricciones sobre la emisión de dinero. Desde entonces, la oferta monetaria global ha crecido exponencialmente, mientras que los salarios reales han estancado. Esta expansión monetaria beneficia a quienes están cerca de la "impresora" (gobiernos, bancos, grandes corporaciones) a expensas del ciudadano común - un fenómeno conocido como el "Efecto Cantillon".',
            'Bitcoin representa un retorno al dinero del mercado libre. No es impuesto por ningún gobierno; emergió orgánicamente de internet. Su suministro está absolutamente limitado por código, haciendo imposible la inflación. Es el dinero más "duro" jamás creado - más escaso incluso que el oro, ya que sabemos exactamente cuánto Bitcoin existirá (21 millones) mientras que no sabemos cuánto oro queda por descubrir en la Tierra o en asteroides.'
          ],
          quiz: [
            {
              question: '¿Qué es la preferencia temporal?',
              options: [
                'Preferir trabajar en ciertos horarios',
                'La tendencia humana a preferir bienes ahora que en el futuro',
                'El tiempo que tardas en gastar tu dinero',
                'La inflación medida en el tiempo'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Qué hace que Bitcoin sea "dinero duro"?',
              options: [
                'Es difícil de usar',
                'Su suministro está absolutamente limitado',
                'Requiere computadoras potentes',
                'Es respaldado por oro'
              ],
              correctAnswer: 1
            }
          ]
        }
      },
      {
        id: '6',
        title: 'Nodos: El Corazón de la Descentralización',
        description: 'Por qué ejecutar un nodo es fundamental para Bitcoin',
        duration: '25 min',
        completed: false,
        content: {
          introduction: 'Los nodos completos son las computadoras que mantienen y validan toda la historia de Bitcoin. Sin ellos, Bitcoin sería solo otra base de datos centralizada. Ejecutar tu propio nodo es ejercer soberanía financiera completa.',
          keyPoints: [
            'Un nodo completo valida todas las transacciones y bloques',
            'No confías, verificas: "Don\'t trust, verify"',
            'Los nodos protegen las reglas del consenso',
            'Cualquiera puede ejecutar un nodo (Raspberry Pi es suficiente)',
            'Más nodos = más descentralización = más resistencia a la censura'
          ],
          detailedContent: [
            'Un nodo completo de Bitcoin descarga y verifica independientemente cada bloque y transacción desde el bloque génesis (3 de enero de 2009) hasta el presente. Actualmente, esto requiere alrededor de 500GB de espacio en disco. El nodo aplica todas las reglas del consenso: verifica firmas digitales, asegura que nadie gaste más Bitcoin del que tiene, confirma que el suministro nunca exceda 21 millones, y valida la prueba de trabajo.',
            'El principio fundamental de Bitcoin es "no confíes, verifica". Sin tu propio nodo, debes confiar en que el nodo de otra persona te está diciendo la verdad sobre tu balance y las transacciones. Con tu propio nodo, verificas matemáticamente todo. Es la diferencia entre confiar en el balance que te muestra tu banco versus contar físicamente tus propios billetes.',
            'Los nodos son los guardianes de las reglas de Bitcoin. Si los mineros intentaran cambiar las reglas (por ejemplo, incrementar el suministro a más de 21 millones), los nodos rechazarían esos bloques como inválidos. Esto sucedió en 2017 durante la "guerra del tamaño de bloque": algunos mineros querían bloques más grandes, pero los nodos de la comunidad se mantuvieron firmes en las reglas existentes.',
            'La belleza de Bitcoin es que ejecutar un nodo completo está al alcance de cualquiera. Una Raspberry Pi 4 con 8GB de RAM y un disco duro externo de 1TB es más que suficiente. Proyectos como Umbrel, RaspiBlitz y MyNode hacen que sea fácil configurar un nodo en casa. El costo total es menos de $200 USD - una pequeña inversión para la soberanía financiera completa.',
            'Cada nodo adicional hace a Bitcoin más resiliente. Si hubiera solo unos pocos nodos, sería fácil para un gobierno cerrarlos. Con decenas de miles de nodos distribuidos globalmente, Bitcoin es prácticamente imposible de censurar o controlar. En países con gobiernos autoritarios, personas ejecutan nodos a través de Tor para mantener el sistema vivo incluso bajo amenaza de persecución.'
          ],
          quiz: [
            {
              question: '¿Qué hace un nodo completo de Bitcoin?',
              options: [
                'Mina nuevos bitcoins',
                'Descarga y verifica independientemente todas las transacciones',
                'Almacena las claves privadas de los usuarios',
                'Controla el precio de Bitcoin'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Cuál es el principio fundamental de Bitcoin respecto a la verificación?',
              options: [
                'Confía en los expertos',
                'Confía en el gobierno',
                'No confíes, verifica',
                'Confía en los mineros'
              ],
              correctAnswer: 2
            }
          ]
        }
      }
    ]
  },
  {
    id: 'intermediate',
    title: 'Módulo 2: Tecnología y Seguridad',
    description: 'Aspectos técnicos profundos del protocolo Bitcoin',
    lessons: [
      {
        id: '5',
        title: 'Minería de Bitcoin Explicada',
        description: 'Comprende cómo se crea nuevo Bitcoin y se asegura la red',
        duration: '25 min',
        completed: false,
        content: {
          introduction: 'La minería de Bitcoin es el proceso mediante el cual se crea nuevo Bitcoin y las transacciones son verificadas y agregadas al blockchain. Es la columna vertebral del modelo de seguridad de Bitcoin.',
          keyPoints: [
            'La minería asegura la red de Bitcoin',
            'Los mineros resuelven rompecabezas matemáticos complejos',
            'Los mineros exitosos reciben recompensas en Bitcoin',
            'La dificultad de minería se ajusta cada 2016 bloques',
            'La prueba de trabajo hace los ataques económicamente inviables'
          ],
          detailedContent: [
            'La minería de Bitcoin cumple dos funciones críticas: crea nuevo Bitcoin según un calendario predecible, y asegura la red haciéndola extremadamente costosa de atacar. Los mineros usan computadoras poderosas para resolver rompecabezas criptográficos, y el primero en encontrar una solución puede agregar el siguiente bloque de transacciones al blockchain.',
            'El proceso de minería implica tomar transacciones pendientes, agruparlas en un bloque, y luego hashear repetidamente el encabezado del bloque con un nonce (un número aleatorio) hasta que el hash resultante cumpla ciertos criterios – específicamente, debe estar por debajo de un valor objetivo. Esto es computacionalmente intensivo pero fácil de verificar una vez encontrado.',
            'Cuando un minero encuentra exitosamente un bloque válido, lo transmite a la red. Otros nodos verifican la solución y agregan el bloque a su copia del blockchain. El minero exitoso recibe una recompensa por bloque (actualmente 3.125 BTC a partir de 2024) más cualquier comisión de transacción de las transacciones incluidas. Esta recompensa es lo que incentiva a los mineros a dedicar recursos computacionales para asegurar la red.',
            'La dificultad de minería se ajusta automáticamente cada 2,016 bloques (aproximadamente dos semanas) para mantener un tiempo promedio de bloque de aproximadamente 10 minutos. Si los bloques se están encontrando demasiado rápido, la dificultad aumenta. Si demasiado lento, disminuye. Este mecanismo autorregulador asegura que Bitcoin mantenga su calendario de emisión predecible independientemente de cuánto poder de minería esté en la red.',
            'El gasto de energía requerido para la minería es frecuentemente criticado, pero es precisamente este costo de energía lo que asegura la red. Un atacante necesitaría gastar más energía (y dinero) que todos los mineros honestos combinados para atacar exitosamente Bitcoin – una hazaña que se vuelve más prohibitiva a medida que la red crece. Esta energía asegura una red monetaria que vale cientos de miles de millones de dólares, procesando millones de transacciones.'
          ]
        }
      },
      {
        id: '6',
        title: 'Anatomía de una Transacción Bitcoin',
        description: 'Cómo funcionan las transacciones a nivel técnico',
        duration: '35 min',
        completed: false,
        content: {
          introduction: 'Una transacción Bitcoin no es simplemente "enviar dinero de A a B". Es una estructura de datos compleja que incluye entradas, salidas, scripts, y firmas digitales. Entender esto te da poder sobre tu dinero.',
          keyPoints: [
            'Las transacciones tienen inputs (entradas) y outputs (salidas)',
            'Modelo UTXO: Unspent Transaction Outputs',
            'El "cambio" en Bitcoin funciona diferente al efectivo',
            'Scripts de Bitcoin permiten condiciones de gasto',
            'Las comisiones incentivan a los mineros a incluir tu transacción'
          ],
          detailedContent: [
            'Bitcoin usa un modelo llamado UTXO (Unspent Transaction Output - Salida de Transacción No Gastada). Piensa en UTXOs como billetes o monedas físicas: no puedes dividir un billete de $20, debes gastarlo completo y recibir cambio. Similarmente, en Bitcoin, si tienes un UTXO de 1 BTC y quieres enviar 0.3 BTC, gastas el UTXO completo de 1 BTC creando dos outputs: 0.3 BTC al destinatario y 0.7 BTC de vuelta a ti como "cambio".',
            'Cada transacción consiste en: 1) Inputs (referencias a UTXOs anteriores que posees), 2) Outputs (nuevos UTXOs que creas para el destinatario y tu cambio), 3) Firmas digitales que prueban que posees los UTXOs que estás gastando, y 4) Metadata como versión de transacción y locktime. La suma de outputs debe ser menor o igual que la suma de inputs; la diferencia es la comisión del minero.',
            'Las direcciones Bitcoin son en realidad scripts - pequeños programas que definen las condiciones bajo las cuales los fondos pueden gastarse. La más común es "Pay-to-Public-Key-Hash" (P2PKH): los fondos pueden gastarse presentando una firma válida de la clave privada correspondiente a un hash de clave pública específico. Pero Bitcoin soporta scripts más complejos como multisig (requiere M-de-N firmas), timelocks (fondos bloqueados hasta cierta fecha), y más.',
            'Las comisiones de transacción no son un porcentaje del monto (como en tarjetas de crédito), sino que se basan en el tamaño en bytes de la transacción. Una transacción simple con 1 input y 2 outputs ocupa alrededor de 250 bytes. Las comisiones se calculan en satoshis por byte (sat/vB). Durante congestión de red, puedes pagar más para que tu transacción sea incluida más rápido; durante periodos tranquilos, comisiones de 1 sat/vB son suficientes.',
            'SegWit (Segregated Witness) fue una actualización importante que separó los datos de firma del resto de la transacción, aumentando efectivamente la capacidad de bloques y habilitando Lightning Network. Las direcciones SegWit (comienzan con bc1) tienen comisiones más bajas que las direcciones legacy. Taproot, activado en 2021, mejoró aún más la privacidad y eficiencia, haciendo que transacciones complejas se vean idénticas a transacciones simples.'
          ],
          quiz: [
            {
              question: '¿Qué es un UTXO?',
              options: [
                'Un tipo de clave privada',
                'Una salida de transacción no gastada',
                'Un protocolo de minería',
                'Una dirección especial'
              ],
              correctAnswer: 1
            },
            {
              question: '¿En qué se basan las comisiones de Bitcoin?',
              options: [
                'En el monto enviado (porcentaje)',
                'En el tamaño de la transacción en bytes',
                'Son fijas sin importar la transacción',
                'Las decide el gobierno'
              ],
              correctAnswer: 1
            }
          ]
        }
      },
      {
        id: '7',
        title: 'Seguridad Operacional: Protegiendo tus Bitcoin',
        description: 'Mejores prácticas para mantener tu Bitcoin seguro',
        duration: '30 min',
        completed: false,
        content: {
          introduction: 'Con Bitcoin, tú eres tu propio banco. Esto significa total control, pero también total responsabilidad. La seguridad operacional (OpSec) adecuada es la diferencia entre soberanía financiera y pérdida catastrófica.',
          keyPoints: [
            'Nunca compartas tu seed phrase con nadie, jamás',
            'Hardware wallets para cantidades significativas',
            'Setups multisig para máxima seguridad',
            'Ataques comunes: phishing, malware, ingeniería social',
            'Herencia: asegura que tus seres queridos puedan acceder a tus fondos'
          ],
          detailedContent: [
            'Tu seed phrase (frase semilla) de 12 o 24 palabras ES tu Bitcoin. Quien tenga acceso a ella tiene acceso completo e irrevocable a tus fondos. Nunca, bajo ninguna circunstancia, debes: 1) Tomarle foto, 2) Guardarla en un archivo digital, 3) Enviárla por email o mensaje, 4) Ingresarla en una computadora conectada a internet, 5) Compartirla con exchanges o "soporte técnico", 6) Almacenarla en servicios de nube. Grábala en metal (productos como Cryptosteel), guárdala en ubicaciones seguras separadas, y considera dividirla usando esquemas como Shamir Secret Sharing.',
            'Para cantidades pequeñas (gastos diarios), una wallet móvil como Phoenix, Muun, o BlueWallet es conveniente. Para cantidades intermedias (ahorros de 3-6 meses), una hardware wallet como Coldcard, Trezor, o Ledger es apropiada. Para cantidades grandes (ahorros de vida), considera multisig: una configuración que requiere múltiples firmas para gastar (ej: 2-de-3 donde tienes dispositivos en diferentes ubicaciones).',
            'Los ataques más comunes son de ingeniería social, no técnicos. Phishing: emails o sitios web falsos que imitan exchanges o wallets legítimas. Malware: software malicioso que reemplaza direcciones Bitcoin en tu portapapeles (intentas copiar-pegar una dirección pero el malware la cambia). Ataques de $5 wrench: alguien te amenaza físicamente para que reveles tus claves. Contra esto: 1) Verifica URLs cuidadosamente, 2) Usa wallets de código abierto y verificable, 3) No alardees públicamente sobre cuánto Bitcoin posees.',
            'La privacidad es un componente crucial de la seguridad. No reutilices direcciones - cada transacción debería usar una dirección nueva. Considera usar herramientas de mejora de privacidad como CoinJoin (Wasabi Wallet, Samourai Whirlpool) para romper el análisis de cadena. Ejecuta tu propio nodo y conéctate a través de Tor para que tu ISP no pueda rastrear tu actividad Bitcoin. Recuerda: Bitcoin no es anónimo por defecto, es pseudónimo - se requiere esfuerzo para mantener privacidad.',
            'La planificación de herencia es crítica pero frecuentemente ignorada. Si mueres sin que nadie sepa cómo acceder a tus Bitcoin, se pierden para siempre. Opciones incluyen: 1) Un testamento tradicional con instrucciones (riesgo: el abogado ve la información), 2) Multisig con miembros de familia de confianza, 3) Servicios especializados como Casa o Unchained Capital que ofrecen soluciones de herencia con multisig, 4) Timelocked transactions que se activan si no refrescas periódicamente. Balancea seguridad con accesibilidad para herederos.'
          ],
          quiz: [
            {
              question: '¿Cuál es la regla más importante sobre tu seed phrase?',
              options: [
                'Compartirla con tu familia',
                'Nunca revelarla a nadie y nunca almacenarla digitalmente',
                'Memorizarla solamente',
                'Enviarla al soporte técnico si tienes problemas'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Qué es multisig?',
              options: [
                'Múltiples tipos de criptomonedas',
                'Una configuración que requiere múltiples firmas para gastar',
                'Un tipo de minería',
                'Una red social para Bitcoin'
              ],
              correctAnswer: 1
            }
          ]
        }
      }
    ]
  },
  {
    id: 'advanced',
    title: 'Módulo 3: Lightning Network y Economía Circular',
    description: 'Aplicaciones avanzadas y adopción práctica en Panamá',
    lessons: [
      {
        id: '8',
        title: 'Lightning Network: Bitcoin Instantáneo',
        description: 'La solución de escalabilidad de capa 2 de Bitcoin',
        duration: '40 min',
        completed: false,
        content: {
          introduction: 'Lightning Network es una red de canales de pago que permite transacciones Bitcoin instantáneas y con comisiones mínimas. Es fundamental para la adopción masiva y la economía circular en Panamá.',
          keyPoints: [
            'Lightning opera "fuera de la cadena" pero está asegurado por Bitcoin',
            'Canales de pago bidireccionales entre participantes',
            'Enrutamiento: puedes pagar a quien no tengas canal directo',
            'Comisiones de fracción de centavo para transacciones',
            'Ideal para micropagos y comercio diario'
          ],
          detailedContent: [
            'El blockchain de Bitcoin puede procesar alrededor de 7 transacciones por segundo (TPS) debido a límites deliberados de tamaño de bloque y tiempo de bloque (10 minutos). Esto es óptimo para seguridad y descentralización, pero insuficiente si miles de millones de personas quisieran hacer transacciones diarias. Lightning Network resuelve esto moviendo la mayoría de transacciones "fuera de la cadena" mientras mantiene la seguridad de Bitcoin.',
            'Un canal Lightning es como un acuerdo bilateral de pago. Tú y tu cafetería favorita abren un canal depositando Bitcoin en una dirección multisig 2-de-2 en la blockchain. Ahora pueden actualizar el balance entre ustedes instantáneamente, ilimitadas veces, sin tocar la blockchain. Cuando deciden cerrar el canal, solo la transacción final de cierre se registra en la blockchain. Si hicieron 1000 cafés durante el mes, solo 2 transacciones on-chain (apertura y cierre) vs 1000.',
            'Lo revolucionario es el enrutamiento: no necesitas canales directos con todos. Si tú tienes canal con Alice, y Alice con Bob, puedes pagar a Bob a través de Alice (quien recibe una pequeña comisión por enrutar). La red Lightning encuentra automáticamente rutas a través de nodos intermediarios. Esto crea una red de pagos global donde, en teoría, puedes pagar a cualquiera con quien la red tenga una ruta, generalmente en pocos saltos.',
            'Las comisiones en Lightning son típicamente menos de 1 satoshi (fracción de centavo de dólar). Esto hace viable casos de uso imposibles en la capa base: streaming de dinero (pagar por segundo de video), micropagos (pagar 10 centavos por leer un artículo), juegos (pequeñas apuestas en tiempo real), propinas (zaps en Nostr). La latencia es sub-segundo vs 10+ minutos de Bitcoin on-chain.',
            'En Bitcoin Life Panamá, Lightning es crucial para la economía circular. Los comercios pueden aceptar pagos sin volatilidad de precios (conversión instantánea a dólares si prefieren), sin comisiones de tarjetas de crédito (3-5%), y con liquidación instantánea (vs días con bancos). Wallets como Phoenix y Muun hacen Lightning tan fácil como WhatsApp. El objetivo es que cualquiera en Panamá pueda pagar su café, restaurante, o mercado con Lightning.'
          ],
          quiz: [
            {
              question: '¿Cuántas transacciones on-chain se necesitan para abrir y cerrar un canal Lightning?',
              options: [
                'Una',
                'Dos (apertura y cierre)',
                'Todas las transacciones del canal',
                'Ninguna'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Cuál es la principal ventaja de Lightning para comercios?',
              options: [
                'Más seguro que Bitcoin base',
                'Comisiones mínimas y liquidación instantánea',
                'No necesita internet',
                'Está regulado por el gobierno'
              ],
              correctAnswer: 1
            }
          ]
        }
      },
      {
        id: '9',
        title: 'Construyendo Economía Circular en Panamá',
        description: 'Estrategias de adopción y orange-pilling de comercios',
        duration: '35 min',
        completed: false,
        content: {
          introduction: 'Bitcoin Life Panamá está construyendo una economía circular donde puedes ganar, gastar, y ahorrar en Bitcoin. Aprende las estrategias para orange-pillear comercios y crear valor real.',
          keyPoints: [
            'Economía circular: cerrar el ciclo de ganar y gastar en Bitcoin',
            'Ventajas para comercios: menos comisiones, sin reversiones, nuevos clientes',
            'Educación es clave: los comercios necesitan entender Bitcoin',
            'BTCMap y directorios para visibilidad',
            'Eventos comunitarios para acelerar adopción'
          ],
          detailedContent: [
            'Una economía circular Bitcoin significa que puedes: 1) Ganar Bitcoin (recibir salario, vender productos/servicios), 2) Gastar Bitcoin (comprar bienes y servicios), 3) Ahorrar Bitcoin (mantener en cold storage). Muchos holders solo hacen #3, creando un "agujero negro" deflacionario. La verdadera adopción requiere #1 y #2, permitiendo que Bitcoin circule y demuestre su utilidad como dinero.',
            'Para comercios, Bitcoin (especialmente Lightning) ofrece ventajas tangibles: Comisiones del 0-1% vs 3-5% de tarjetas de crédito (en un negocio con márgenes del 10%, esto duplica la ganancia neta). Sin reversiones de cargo (fraude común en tarjetas donde el cliente disputa y recupera dinero). Liquidación instantánea vs 2-3 días de procesadores de pago. Acceso a clientes globales sin restricciones geográficas. Privacidad financiera (el banco no ve tus ventas). Marketing gratis (comunidad Bitcoin promociona comercios que aceptan BTC).',
            'El proceso de orange-pillear un comercio: 1) Establecer relación (ser cliente regular), 2) Educar sobre Bitcoin suavemente (regala libro, invita a evento), 3) Demostrar beneficios concretos (muestra la diferencia en comisiones), 4) Facilitar implementación (ayuda con wallet setup, punto de venta), 5) Hacer primera venta (tú compras con Bitcoin), 6) Soporte continuo (estar disponible para preguntas), 7) Visibilidad (agregar a BTCMap, promover en redes). La paciencia y educación genuina son fundamentales.',
            'Herramientas tecnológicas para comercios: BTCPay Server (procesador de pagos auto-hospedado gratuito), OpenNode o Strike (procesadores con conversión automática a fiat), Breez o LNbits (soluciones Lightning para punto de venta), Zaprite (facturación en Bitcoin). Para comercios pequeños, una simple wallet Lightning como Phoenix en una tablet dedicada puede ser suficiente para empezar.',
            'Bitcoin Life Panamá organiza: Meetups semanales (educación y networking), Bitcoin Walks (visitar comercios que aceptan Bitcoin), Hackatones (desarrollar soluciones locales), Talleres para comercios (capacitación técnica), Conferencias (traer expertos internacionales). El objetivo es crear masa crítica: cuando suficientes comercios aceptan Bitcoin, la utilidad de tenerlo aumenta exponencialmente (efecto de red), acelerando adopción orgánicamente. Panamá puede convertirse en hub Bitcoin de Latinoamérica.'
          ],
          quiz: [
            {
              question: '¿Qué es una economía circular Bitcoin?',
              options: [
                'Solo comprar y holdear Bitcoin',
                'Ganar, gastar y ahorrar en Bitcoin cerrando el ciclo',
                'Comerciar Bitcoin por altcoins',
                'Minar Bitcoin en círculos'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Cuál es una ventaja clave de Bitcoin para comercios?',
              options: [
                'Comisiones mucho menores que tarjetas de crédito',
                'El gobierno subsidia las ventas',
                'No necesitas dar recibos',
                'Puedes cobrar más caro'
              ],
              correctAnswer: 0
            }
          ]
        }
      },
      {
        id: '10',
        title: 'El Futuro: Escalabilidad e Innovación',
        description: 'Hacia dónde se dirige Bitcoin y su ecosistema',
        duration: '30 min',
        completed: false,
        content: {
          introduction: 'Bitcoin sigue evolucionando. Nuevas tecnologías como Taproot, RGB, y proyectos de capa 2/3 están expandiendo lo que es posible construir sobre la base sólida de Bitcoin.',
          keyPoints: [
            'Taproot: privacidad y contratos inteligentes mejorados',
            'RGB y Taro: activos y tokens en Bitcoin',
            'Fedimint: bancos comunitarios con privacidad',
            'Ark: pools de liquidez para pagos',
            'Bitcoin como capa de liquidación final global'
          ],
          detailedContent: [
            'Taproot, activado en noviembre 2021, fue la actualización más significativa de Bitcoin desde SegWit. Introduce firmas Schnorr (más eficientes que ECDSA), agregación de claves (múltiples firmantes se ven como uno), y MAST (Merkelized Abstract Syntax Trees) que permite scripts complejos sin revelar condiciones no usadas. Resultado: transacciones multisig, contratos inteligentes, y pagos simples se ven idénticos en la blockchain, mejorando dramáticamente la privacidad.',
            'RGB es un protocolo de contratos inteligentes que opera completamente fuera de la blockchain Bitcoin usando client-side validation. Permite emitir tokens, NFTs, stablecoins, y ejecutar contratos complejos sin saturar la blockchain. Similarmente, Taro (ahora rebranded como Taproot Assets) permite activos en Lightning Network. Esto podría llevar a stablecoins en dólares sobre Lightning, facilitando la adopción en países con preferencia por dólares mientras aprovechando la infraestructura Bitcoin.',
            'Fedimint son federaciones comunitarias que operan como "bancos" custodiales con características especiales: usan eCash (protocolo Chaumian) que proporciona privacidad absoluta incluso de la federación, requieren supermayorías para robos (dificulta corrupción interna), y pueden integrarse con Lightning para intercambio sin confianza con el resto del mundo. Útil para comunidades que necesitan compartir custodia pero quieren mejor privacidad que soluciones bancarias tradicionales.',
            'Ark es un protocolo experimental que crea "pools" de liquidez compartidos. Usuarios depositan Bitcoin en pools de uso común que pueden resolver pagos internamente sin transacciones on-chain ni canales Lightning individuales. Promete escalabilidad extrema (millones de usuarios compartiendo UTXOs) mientras mantiene autocustodia eventual (puedes sacar tu dinero unilateralmente). Aún en desarrollo pero podría complementar Lightning para casos de uso específicos.',
            'La visión a largo plazo de Bitcoin no es reemplazar Visa directamente en la capa base, sino servir como la capa de liquidación final del sistema financiero global. Bancos centrales podrían liquidar entre ellos en Bitcoin, fintechs construir servicios sobre Lightning, remesas fluir globalmente sin intermediarios. Bitcoin se convierte en el "oro digital" sobre el cual se construye todo lo demás, similar a cómo el oro respaldaba el sistema monetario del siglo XX pero las transacciones diarias usaban billetes y cheques.'
          ],
          quiz: [
            {
              question: '¿Qué mejora Taproot en Bitcoin?',
              options: [
                'Velocidad de minería',
                'Privacidad y eficiencia de contratos inteligentes',
                'Suministro máximo',
                'Precio de Bitcoin'
              ],
              correctAnswer: 1
            },
            {
              question: '¿Cuál es la visión a largo plazo de Bitcoin?',
              options: [
                'Reemplazar todo el efectivo',
                'Servir como capa de liquidación final del sistema financiero global',
                'Competir con las redes sociales',
                'Solo para especuladores'
              ],
              correctAnswer: 1
            }
          ]
        }
      }
    ]
  }
];

const Lessons = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId?: string }>();
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResults, setShowResults] = useState(false);

  const course = coursesData.find(c => c.id === courseId);
  const currentLesson = course?.lessons.find(l => l.id === lessonId);
  const currentLessonIndex = course?.lessons.findIndex(l => l.id === lessonId) ?? -1;
  const nextLesson = course?.lessons[currentLessonIndex + 1];
  const prevLesson = course?.lessons[currentLessonIndex - 1];

  useSeoMeta({
    title: currentLesson ? `${currentLesson.title} - Bitcoin Learn` : `${course?.title} - Bitcoin Learn`,
    description: currentLesson?.description || course?.description || 'Aprende Bitcoin',
  });

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Curso no encontrado</h1>
          <Link to="/">
            <Button>Volver al Inicio</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Course overview (no specific lesson selected)
  if (!currentLesson) {
    const completedLessons = course.lessons.filter(l => l.completed).length;
    const progress = (completedLessons / course.lessons.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <Link to="/">
                <Button variant="ghost" className="mb-4">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al Inicio
                </Button>
              </Link>

              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-white">
                    {course.title}
                  </h1>
                  <p className="text-xl text-gray-600 dark:text-gray-400">
                    {course.description}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center">
                  <Book className="w-8 h-8 text-white" />
                </div>
              </div>

              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-4">
                <div className="flex items-center gap-2">
                  <Book className="w-4 h-4" />
                  {course.lessons.length} lecciones
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {course.lessons.reduce((total, lesson) => {
                    const minutes = parseInt(lesson.duration);
                    return total + minutes;
                  }, 0)} minutos en total
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  {completedLessons}/{course.lessons.length} completadas
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Progreso del Curso</span>
                  <span className="text-muted-foreground">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-3" />
              </div>
            </div>

            {/* Lessons List */}
            <div className="space-y-4">
              {course.lessons.map((lesson, index) => (
                <Card
                  key={lesson.id}
                  className="group hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-orange-200 dark:hover:border-orange-800"
                >
                  <Link to={`/lessons/${courseId}/${lesson.id}`}>
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-lg">
                            {index + 1}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <CardTitle className="group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors mb-2">
                                {lesson.title}
                              </CardTitle>
                              <CardDescription className="line-clamp-2">
                                {lesson.description}
                              </CardDescription>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {lesson.completed ? (
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300">
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                  Completada
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  <Circle className="w-3.5 h-3.5 mr-1" />
                                  Comenzar
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {lesson.duration}
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {lesson.content.keyPoints.length} puntos clave
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Individual lesson view
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Navigation */}
          <div className="mb-8">
            <Link to={`/lessons/${courseId}`}>
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver a {course.title}
              </Button>
            </Link>

            {/* Progress indicator */}
            <div className="flex items-center gap-2 mb-4">
              {course.lessons.map((lesson, idx) => (
                <div
                  key={lesson.id}
                  className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                    idx <= currentLessonIndex
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                      : 'bg-gray-200 dark:bg-gray-800'
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground mb-6">
              <span>Lección {currentLessonIndex + 1} de {course.lessons.length}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {currentLesson.duration}
              </span>
            </div>
          </div>

          {/* Lesson Content */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                  {currentLessonIndex + 1}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-3xl mb-2">{currentLesson.title}</CardTitle>
                  <CardDescription className="text-base">{currentLesson.description}</CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <Tabs defaultValue="content" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="content">
                    <FileText className="w-4 h-4 mr-2" />
                    Contenido
                  </TabsTrigger>
                  <TabsTrigger value="quiz" disabled={!currentLesson.content.quiz}>
                    <Award className="w-4 h-4 mr-2" />
                    Cuestionario
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="content" className="space-y-6">
                  {/* Introduction */}
                  <div>
                    <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300">
                      {currentLesson.content.introduction}
                    </p>
                  </div>

                  <Separator />

                  {/* Key Points */}
                  <div>
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Coins className="w-5 h-5 text-orange-600" />
                      Puntos Clave
                    </h3>
                    <ul className="space-y-3">
                      {currentLesson.content.keyPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Separator />

                  {/* Detailed Content */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold mb-4">Inmersión Profunda</h3>
                    {currentLesson.content.detailedContent.map((paragraph, idx) => (
                      <p key={idx} className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="quiz" className="space-y-6">
                  {currentLesson.content.quiz && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-2">Evalúa Tu Conocimiento</h3>
                        <p className="text-muted-foreground">
                          Responde estas preguntas para verificar tu comprensión
                        </p>
                      </div>

                      {currentLesson.content.quiz.map((q, qIdx) => (
                        <Card key={qIdx} className="border-2">
                          <CardHeader>
                            <CardTitle className="text-lg">
                              Pregunta {qIdx + 1}: {q.question}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {q.options.map((option, oIdx) => (
                              <button
                                key={oIdx}
                                onClick={() => {
                                  setSelectedAnswer(oIdx);
                                  setShowResults(true);
                                }}
                                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                                  showResults && oIdx === q.correctAnswer
                                    ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                                    : showResults && selectedAnswer === oIdx
                                    ? 'border-red-500 bg-red-50 dark:bg-red-950/30'
                                    : selectedAnswer === oIdx
                                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
                                    : 'border-gray-200 dark:border-gray-800 hover:border-orange-300 dark:hover:border-orange-700'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                    showResults && oIdx === q.correctAnswer
                                      ? 'border-green-500 bg-green-500'
                                      : showResults && selectedAnswer === oIdx
                                      ? 'border-red-500 bg-red-500'
                                      : selectedAnswer === oIdx
                                      ? 'border-orange-500 bg-orange-500'
                                      : 'border-gray-300 dark:border-gray-700'
                                  }`}>
                                    {showResults && oIdx === q.correctAnswer && (
                                      <CheckCircle2 className="w-4 h-4 text-white" />
                                    )}
                                  </div>
                                  <span>{option}</span>
                                </div>
                              </button>
                            ))}

                            {showResults && (
                              <div className={`p-4 rounded-lg ${
                                selectedAnswer === q.correctAnswer
                                  ? 'bg-green-50 dark:bg-green-950/30 border-2 border-green-200 dark:border-green-800'
                                  : 'bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-800'
                              }`}>
                                <p className="font-semibold">
                                  {selectedAnswer === q.correctAnswer ? '✓ ¡Correcto!' : '✗ Incorrecto'}
                                </p>
                                <p className="text-sm mt-1">
                                  {selectedAnswer === q.correctAnswer
                                    ? '¡Buen trabajo! Comprendes este concepto.'
                                    : `La respuesta correcta es: ${q.options[q.correctAnswer]}`
                                  }
                                </p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}

                      {showResults && (
                        <Button
                          onClick={() => {
                            setSelectedAnswer(null);
                            setShowResults(false);
                          }}
                          variant="outline"
                          className="w-full"
                        >
                          Intentar de Nuevo
                        </Button>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            {prevLesson ? (
              <Link to={`/lessons/${courseId}/${prevLesson.id}`}>
                <Button variant="outline" size="lg">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Lección Anterior
                </Button>
              </Link>
            ) : (
              <div />
            )}

            {nextLesson ? (
              <Link to={`/lessons/${courseId}/${nextLesson.id}`}>
                <Button size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700">
                  Siguiente Lección
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to={`/lessons/${courseId}`}>
                <Button size="lg" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                  <Award className="w-4 h-4 mr-2" />
                  Completar Curso
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lessons;
